import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ColorLibraryIcon from "../../../../assets/icons/ColorLibrary";
import SegmentPreview from "../../../../assets/icons/SegmentPreview";
import WheelPreview from "../../../../assets/icons/WheelPreview";
import { useSegmentPalettes } from "../../../../hooks/useSegmentPalettes";
import useSegmentSettings from "../../../../hooks/useSegmentSetting";
import {
  setColorPaletteDefault,
  setSelectedPaletteTab,
} from "../../../../redux/reducers/colorPalette";
import { openModal } from "../../../../redux/reducers/dialog";
import {
  pushToHistoryAction,
  setWheelColorPalette,
  updateSegmentColorPalette,
} from "../../../../redux/reducers/segmentBuilder";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getPaletteColors } from "../../../../redux/selectors/colorPalette";
import {
  getSelectedSegment,
  getWheelId,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { savePalette } from "../../../../services/api";
import { ASSETS } from "../../../../utils/config";
import Button from "../../../Button";
import Input from "../../../Input";
import TextView from "../../../TextView";
import CustomTabs from "../CustomTabs";
import ColorLibrary from "./ColorLibrary";
import { PALETTE_TYPES } from "./constants";
import Image from "./Image";
import Prompt from "./Prompt";
import ManualPalette from "./ManualPalette";
import HexTab from "./HexTab";
import {
  PALETTE_MANUAL_BUILDER,
  PALETTE_MODAL_V2_ENABLED,
} from "../../../../constants/featureFlags";
import * as Styled from "./styles";

// D-784: humanize the interpolated palette-type slug for user-facing copy
// ("segment" -> "Segment"). Gated by PALETTE_MODAL_V2_ENABLED at the call site.
const humanizePaletteType = (t) =>
  t === PALETTE_TYPES.WHEEL ? "Wheel" : "Segment";

// API KEYS
// usage type 1 for wheel, 2 for segment
// tab type 1 for prompt, 2 for image, 3 for color
// type 1 for save, 2 for recent


export default function ColorPaletteModal({
  onClose,
  paletteType = PALETTE_TYPES.SEGMENT,
  isWheelWizard = false,
  onPaletteChange,
}) {
  const [showLibrary, setShowLibrary] = useState(false);
  const wheelId = useSelector(getWheelId);
  const dispatch = useDispatch();
  const selectedTab = useSelector(
    (state) => state.colorPalette.selectedPaletteTab
  );
  const selectedArc = useSelector(getSelectedSegment) || {};
  const { settings: segmentSettings = {} } = selectedArc;
  const { color: initialSegmentColor } = segmentSettings;
  const { handleChange } = useSegmentSettings();
  const [searchText, setSearchText] = useState("");
  const { updateSegmentColors } = useSegmentPalettes();

  const types = ["prompt", "image", "color"];

  const paletteColors = useSelector((state) =>
    getPaletteColors(state, types[selectedTab], paletteType)
  );

  const selectedPalette = paletteColors.find((item) => item.selected);

  useEffect(() => {
    if (onPaletteChange && selectedPalette) {
      onPaletteChange(selectedPalette);
    }
  }, [selectedPalette, onPaletteChange]);

  const handleTabChange = (e, newVal) => {
    dispatch(setSelectedPaletteTab(newVal));
  };

  const handleConfirm = () => {
    // Nemesis P1: guard against a Select with nothing generated/selected. The
    // SEGMENT branch below reads `selectedPalette.colors[0]?.hex` before any
    // check, so an undefined selection throws an uncaught TypeError (latent on
    // the segment path); the wheel path silently no-ops inside the try/catch.
    // Mirror ColorLibrary.jsx's guard: one honest toast, no crash. (react-toastify
    // `toast` already imported.)
    if (!selectedPalette) {
      toast.error("Select a palette first");
      return;
    }
    if (isWheelWizard) {
      handleSelectPalette();
      return;
    }
    if (
      paletteType == PALETTE_TYPES.SEGMENT &&
      selectedPalette.colors[0]?.hex !== initialSegmentColor
    )
      dispatch(
        openModal({
          onConfirm: handleSelectPalette,
          title: "Change Segment Color",
          subtitle:
            "This Palette will change the Segment's Color. Proceed anyway?",
        })
      );
    else if (paletteType == PALETTE_TYPES.WHEEL && !isWheelWizard)
      dispatch(
        openModal({
          onConfirm: handleSelectPalette,
          title: "Change Wheel Palette",
          subtitle:
            "Changing the Wheel Palette will update the color palettes for each Segment as well. This can take several seconds, but will occur in the background as you continue to build. Still Proceed?",
        })
      );
    else handleSelectPalette();
  };

  const handleSelectPalette = async () => {
    const TAB_ENUMS = {
      0: 1, // Prompt
      1: 3, // Image
      2: 2, // Color
    };
    try {
      // Call the savePalette API with the selected palette
      const response = await savePalette({
        id: typeof selectedPalette.id === 'string' ? undefined : selectedPalette.id,
        colors: selectedPalette.colors,
        palette_name: selectedPalette.paletteName,
        wheel_id: wheelId,
        type: 2,
        usage_type: paletteType == PALETTE_TYPES.SEGMENT ? 2 : 1,
        tab_type: TAB_ENUMS[selectedTab],
        metadata: {
          prompt: selectedTab == 0 ? selectedPalette.prompt : undefined,
          image: selectedTab == 1 ? selectedPalette.imageUrl : undefined,
          color: selectedTab == 2 ? selectedPalette.colors[0]?.hex : undefined,
        },
      });

      if (response.data.success) {
        const paletteId = response.data.data.id;
        dispatch(pushToHistoryAction());

        if (paletteType == PALETTE_TYPES.SEGMENT) {
          handleChange("segmentColor", null, selectedPalette.colors[0]?.hex);

          dispatch(
            updateSegmentColorPalette({
              palette: selectedPalette,
              paletteId,
              id: selectedArc?.id,
            })
          );
        } else {
          dispatch(
            setWheelColorPalette({
              paletteData: selectedPalette,
              paletteId,
            })
          );
          toast.success(
            isWheelWizard
              ? "Palette created for your wheel."
              : "Wheel palette updated successfully"
          );

          updateSegmentColors(selectedPalette);
        }

        // Reset the palette types in the reducer
        dispatch(setColorPaletteDefault({ paletteType }));
        onClose();
      } else {
        // Optionally handle API failure
        console.error("Failed to save palette. Please try again.");
      }
    } catch (error) {
      console.error("Error saving palette:", error);
    }
  };

  const tabOptions = new Map([
    [
      "0",
      {
        label: "Prompt",
        tooltip: "Prompt",
        component: (
          <Prompt
            paletteType={paletteType}
            handleSelect={handleConfirm}
            showSelectButton={isWheelWizard && !!selectedPalette}
          />
        ),
      },
    ],
    [
      "1",
      {
        label: "Image",
        tooltip: "Image",
        component: (
          <Image
            paletteType={paletteType}
            handleSelect={handleConfirm}
            showSelectButton={isWheelWizard && !!selectedPalette}
          />
        ),
      },
    ],
  ]);

  // D15 Palette Modal V2: fold the deck-faithful hex builder in as the third
  // "Hex" tab (Prompt | Image | Hex). It reuses the "color" bucket (tab index 2)
  // so the modal's single Select button commits it. When V2 is ON the legacy
  // ManualPalette draft is retired (never rendered); when OFF the modal keeps its
  // existing Prompt/Image (+ optional Manual-draft) tabs byte-identical to today.
  if (PALETTE_MODAL_V2_ENABLED) {
    tabOptions.set("2", {
      label: "Hex",
      tooltip: "Hex",
      component: (
        <HexTab
          paletteType={paletteType}
          handleSelect={handleConfirm}
          showSelectButton={isWheelWizard && !!selectedPalette}
        />
      ),
    });
  } else if (PALETTE_MANUAL_BUILDER) {
    // Manual Brand Palette builder (pick-your-exact-hex mode). Flag-gated draft:
    // when REACT_APP_PALETTE_MANUAL_BUILDER is OFF (default), this tab is never
    // added and the modal renders exactly the existing Prompt/Image auto tabs.
    tabOptions.set("2", {
      label: "Manual",
      component: (
        <ManualPalette
          paletteType={paletteType}
          handleSelect={handleConfirm}
          showSelectButton={isWheelWizard && !!selectedPalette}
        />
      ),
    });
  }

  return (
    <Styled.ModalContainer
      style={
        !isWheelWizard
          ? {
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              height: "75vh",
            }
          : null
      }
    >
      {isWheelWizard && (
        <Styled.LibraryButton
          onClick={() => setShowLibrary(!showLibrary)}
          title={showLibrary ? "Go Back" : "Library"}
          style={{ position: "absolute", top: 10, right: 10 }}
        >
          {showLibrary ? <ArrowBackIcon /> : <ColorLibraryIcon />}{" "}
          <TextView>{showLibrary ? "Go Back" : "Library"}</TextView>
        </Styled.LibraryButton>
      )}
      {!isWheelWizard && (
        <>
          {" "}
          <Styled.ModalHeader>
            <div>
              {showLibrary ? (
                <Styled.Row>
                  <IconButton onClick={() => setShowLibrary(false)} title="Back">
                    <ArrowBackIcon style={{ color: "#000" }} />
                  </IconButton>
                  <div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "28px 1fr",
                        alignItems: "center",
                      }}
                    >
                      <ColorLibraryIcon color="black" />
                      <TextView variant="title">Color Library</TextView>
                    </div>
                    <TextView>
                      Store and organize color palettes for future use!
                    </TextView>
                  </div>
                </Styled.Row>
              ) : (
                <>
                  <TextView variant="title">Palette Generator</TextView>
                  <TextView>
                    Generate unlimited color palettes for your{" "}
                    {PALETTE_MODAL_V2_ENABLED
                      ? humanizePaletteType(paletteType)
                      : paletteType}
                    .
                  </TextView>
                </>
              )}
            </div>
            {showLibrary ? (
              <Input
                variant={"secondary"}
                isSearch={true}
                containerStyle={{ marginBottom: 0 }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            ) : (
              <Styled.Row>
                <Styled.LibraryButton
                  onClick={() => setShowLibrary(true)}
                  title="Library"
                >
                  <ColorLibraryIcon />
                  <TextView>{"Library"}</TextView>
                </Styled.LibraryButton>

                <IconButton onClick={onClose} title="Close">
                  <CloseIcon />
                </IconButton>
              </Styled.Row>
            )}
          </Styled.ModalHeader>
        </>
      )}

      {showLibrary ? (
        <ColorLibrary
          onClose={onClose}
          onBack={() => setShowLibrary(false)}
          searchText={searchText}
          setSearchText={setSearchText}
          paletteType={paletteType}
          isWheelWizard={isWheelWizard}
          onPaletteChange={onPaletteChange}
        />
      ) : (
        <>
          <Styled.ModalContent>
            <Styled.Grid>
              <Styled.PreviewContainer>
                <TextView variant="subtitle" style={{ fontWeight: 700 }}>
                  Preview
                </TextView>
                <TextView>Generated color palette will preview here!</TextView>
                <Styled.ImageWrapper
                  style={
                    paletteType == PALETTE_TYPES.WHEEL ? { padding: 20 } : {}
                  }
                >
                  {paletteType == PALETTE_TYPES.SEGMENT ? (
                    selectedPalette?.colors.length ? (
                      <SegmentPreview colors={selectedPalette?.colors} />
                    ) : (
                      <img
                        src={ASSETS.SegmentPreview}
                        style={{ width: "100%", margin: "12px 0px" }}
                      />
                    )
                  ) : (
                    <WheelPreview colors={selectedPalette?.colors} />
                  )}
                </Styled.ImageWrapper>
              </Styled.PreviewContainer>
              <Styled.StylingContainer>
                <CustomTabs
                  selectedTab={selectedTab}
                  handleTabChange={handleTabChange}
                  options={tabOptions}
                />
              </Styled.StylingContainer>
            </Styled.Grid>
          </Styled.ModalContent>
          {!isWheelWizard && (
            <Styled.ModalFooter>
              <Styled.ButtonsContainer>
                <Button variant={"secondary"} onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={handleConfirm}>Select</Button>
              </Styled.ButtonsContainer>
            </Styled.ModalFooter>
          )}
        </>
      )}
    </Styled.ModalContainer>
  );
}
